import React, { useCallback, useMemo, useRef, useState } from 'react';

import { BdsModal, BdsUpload } from 'blip-ds/dist/blip-ds-react';
import { ModalContent, FileUploadContainer } from './styles';
import { KnowledgeBaseApi } from '../../../../api/KnowledgeBaseApi';
import { showToast } from '../../../../api/CommonApi';
import { useContentLocalizer } from '../../../../hooks/useContentLocalizer';
import { useModalManagement } from '../../../../hooks/useModalManagement';
import { useKnowledgeBaseTracking } from '../../../../hooks/useKnowledgeBaseTracking';
import { ModalHeader } from '../../../../components/ModalHeader';
import { ModalFooter } from '../../../../components/ModalFooter';
import { localization } from './localization';
import { getLanguageCode } from 'utils/language';
import { useCommon } from 'contexts/CommonContext';
import { languages } from 'blip-ds/dist/types/utils/languages';
import { ACCEPTED_FILE_TYPES } from '../../constants';
import { useFileErrorHandler } from 'hooks/useFileErrorHandler';
import { KNOWLEDGE_CONTENT_REFRESH, KNOWLEDGE_CONTENT_CANCEL } from '../../../../shared/SegmentEvents';

interface UpdateFileModalProps {
  isOpen: boolean;
  tenantId: string;
  catalogId: string;
  documentId: string;
  onClose: (wasCancelled: boolean) => void;
}

const UpdateFileModal: React.FC<UpdateFileModalProps> = ({ isOpen, tenantId, catalogId, documentId, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const uploadRf = useRef<HTMLBdsUploadElement>(null);
  const { handleFileError } = useFileErrorHandler();
  const translate = useContentLocalizer(localization);
  const { trackKnowledgeBaseEvent } = useKnowledgeBaseTracking();
  const {
    common: { language },
  } = useCommon();

  // Memoize language code calculation
  const languageCode = useMemo(() => {
    return getLanguageCode(language) as languages;
  }, [language]);

  const resetForm = useCallback(() => {
    setSelectedFile(null);
    uploadRf.current?.deleteAllFiles();
    setIsLoading(false);
  }, []);

  const handleTrackingCancel = useCallback(() => {
    if (isLoading) return;
    trackKnowledgeBaseEvent(KNOWLEDGE_CONTENT_CANCEL);
  }, [isLoading, trackKnowledgeBaseEvent]);

  const { modalRef, handleCancel } = useModalManagement({
    isOpen,
    onClose,
    resetForm,
    onCancel: handleTrackingCancel,
    shouldAllowCancel: !isLoading,
  });

  const handleFileUpload = useCallback((event: CustomEvent) => {
    const file = event.detail.value[0];
    setSelectedFile(file);
  }, []);

  const handleUpdateFile = useCallback(async () => {
    if (!isFormValid || isLoading || !selectedFile) {
      return;
    }

    try {
      setIsLoading(true);
      await KnowledgeBaseApi.createDocumentVersionAsync(tenantId, catalogId, documentId, selectedFile);

      trackKnowledgeBaseEvent(KNOWLEDGE_CONTENT_REFRESH, {
        knowcontName: selectedFile.name,
        knowcontType: selectedFile.name.split('.').pop() ?? 'unknown',
      });

      showToast({
        title: translate.successToast.title,
        message: translate.successToast.message,
        type: 'success',
      });

      resetForm();
      onClose(false);
    } catch (error) {
      const consoleError = 'Error updating file:';
      handleFileError(consoleError, translate.errorToast, error as string);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedFile,
    tenantId,
    catalogId,
    documentId,
    onClose,
    resetForm,
    isLoading,
    translate,
    trackKnowledgeBaseEvent,
    handleFileError,
  ]);

  const isFormValid = selectedFile !== null;

  return (
    <BdsModal
      ref={modalRef}
      open={isOpen}
      size="dynamic"
      closeButton={!isLoading}
      outzoneClose={!isLoading}
      enterClose={false}
    >
      <ModalContent direction="column" gap="3">
        <ModalHeader title={translate.title} subtitle={translate.subtitle} />

        <FileUploadContainer $isLoading={isLoading}>
          <BdsUpload
            language={languageCode}
            ref={uploadRf}
            titleName={translate.fileUpload.title}
            subtitle={translate.fileUpload.subtitle}
            dataAccept={ACCEPTED_FILE_TYPES}
            multiple={false}
            onBdsUploadChange={handleFileUpload}
          />
        </FileUploadContainer>

        <ModalFooter
          cancelText={translate.cancelButton}
          saveText={translate.saveButton}
          isLoading={isLoading}
          isFormValid={isFormValid}
          onCancel={handleCancel}
          onSave={handleUpdateFile}
        />
      </ModalContent>
    </BdsModal>
  );
};

export default UpdateFileModal;
